import React, { useState, useEffect, useRef } from 'react'
import { supabase } from './lib/supabase'
import Login from './components/Login'
import MemberDashboard from './components/MemberDashboard'
import AdminDashboard from './components/AdminDashboard'

// ── Iframe lock ───────────────────────────────────────────────────────────────
// The app is designed to run embedded inside the Squarespace members page.
// If someone navigates directly to the Netlify URL, show a redirect nudge.
const FLOCK_URL  = 'https://shepherdshutwines.com.au/the-flock-home'
const isEmbedded = window.self !== window.top

// ── Squarespace identity check ────────────────────────────────────────────────
// When the app is embedded in the Squarespace members page, verify the persisted
// Supabase session belongs to the CURRENTLY logged-in Squarespace customer.
// This closes the account-switch bug: if user A logged into Flock and later
// user B logs into Squarespace, the stale session is cleared before B sees it.
//
// Protocol (pull model — iframe initiates so there's no race on the listener):
//   1. App posts FLOCK_READY to parent with a strict targetOrigin.
//   2. Squarespace code injection (footer) responds with SS_IDENTITY { email }.
//   3. App compares emails:
//        match   → keep session, proceed normally
//        mismatch → sign out the stale session silently
//        unknown  → SS user can't be read or isn't logged in → leave alone
//
// Security: postMessage can only CLEAR a session, never create one. Data access
// still requires a real Supabase OTP. A forged message can cause a sign-out
// (annoying) but cannot expose another member's data (origin-checked both ways).

const SS_PARENT_ORIGIN   = 'https://shepherdshutwines.com.au'
const SS_IDENTITY_TIMEOUT = 2000   // ms — if parent doesn't reply, leave session alone

function checkSSIdentity(sessionEmail) {
  return new Promise((resolve) => {
    if (!isEmbedded) { resolve('unknown'); return }

    let done = false

    function handler(event) {
      if (event.origin !== SS_PARENT_ORIGIN) return
      if (!event.data || event.data.type !== 'SS_IDENTITY') return
      done = true
      window.removeEventListener('message', handler)
      clearTimeout(timer)

      const ssEmail = event.data.email
      if (!ssEmail) {
        // Squarespace customer not logged in or email not readable — leave alone.
        resolve('unknown')
      } else if (ssEmail.toLowerCase() === sessionEmail.toLowerCase()) {
        resolve('match')
      } else {
        resolve('mismatch')
      }
    }

    // Safety timeout: if the parent code injection is absent or slow, default
    // to leaving the session alone rather than wrongly logging everyone out.
    const timer = setTimeout(() => {
      if (!done) {
        window.removeEventListener('message', handler)
        resolve('unknown')
      }
    }, SS_IDENTITY_TIMEOUT)

    window.addEventListener('message', handler)
    // Ask the parent for the current Squarespace customer identity.
    window.parent.postMessage({ type: 'FLOCK_READY' }, SS_PARENT_ORIGIN)
  })
}

export default function App() {
  const [session,  setSession]  = useState(null)
  const [userType, setUserType] = useState(null)   // 'admin' | 'member' | 'unknown' | null
  const [userData, setUserData] = useState(null)
  const [loading,  setLoading]  = useState(true)

  // True once we've resolved the signed-in user at least once. Subsequent auth
  // events (token auto-refresh on window/tab focus) then re-resolve in the
  // BACKGROUND without flipping `loading` — which would unmount the dashboard
  // and reset its open tab / edit modal. Reset on sign-out.
  const resolvedRef = useRef(false)

  useEffect(() => {
    // Subscribe before getSession so we never miss a fast-firing SIGNED_IN event.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (event === 'SIGNED_OUT') {
        resolvedRef.current = false
        setSession(null)
        setUserType(null)
        setUserData(null)
        setLoading(false)
        return
      }

      setSession(newSession)
      if (newSession) {
        resolveUserType(newSession.user)
      } else {
        setLoading(false)
      }
    })

    // Hydrate existing persisted session (localStorage refresh token).
    supabase.auth.getSession().then(({ data: { session: s }, error }) => {
      if (error) {
        supabase.auth.signOut().finally(() => setLoading(false))
        return
      }
      // onAuthStateChange will fire and call resolveUserType — no duplicate call needed.
      if (!s) setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function resolveUserType(user) {
    // Spinner only on the FIRST resolve; background-refresh silently thereafter
    // so the dashboard (and any open modal) isn't torn down on window focus.
    if (!resolvedRef.current) setLoading(true)
    resolvedRef.current = true

    // ── SS identity check (embedded only) ────────────────────────────────────
    // Runs before any DB calls. If the persisted session belongs to a different
    // Squarespace customer, sign out silently. The SIGNED_OUT event fired by
    // signOut() will clear all state and set loading=false — no extra cleanup needed.
    if (isEmbedded) {
      const check = await checkSSIdentity(user.email)
      if (check === 'mismatch') {
        console.log('[App] SS identity mismatch — clearing stale Flock session')
        await supabase.auth.signOut()
        return
      }
    }

    try {
      // ── Admin check ───────────────────────────────────────────────────────────
      const { data: adminRow, error: adminErr } = await supabase
        .from('admin_users')
        .select('id, email')
        .eq('user_id', user.id)
        .maybeSingle()

      if (isJwtError(adminErr)) { await supabase.auth.signOut(); setLoading(false); return }

      if (adminRow) {
        setUserType('admin')
        setUserData(adminRow)
        setLoading(false)
        return
      }

      // ── Member check (by user_id) ─────────────────────────────────────────────
      const { data: memberRow, error: memberErr } = await supabase
        .from('members')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (isJwtError(memberErr)) { await supabase.auth.signOut(); setLoading(false); return }

      if (memberRow) {
        setUserType('member')
        setUserData(memberRow)
        setLoading(false)
        return
      }

      // ── Email fallback link ───────────────────────────────────────────────────
      // poll-orders creates member rows with user_id = null (member hasn't
      // authenticated yet). On first OTP sign-in, link by email via SECURITY
      // DEFINER RPC and return the now-linked row.
      const { data: linked, error: linkErr } = await supabase.rpc('link_member_to_auth_user')

      if (isJwtError(linkErr)) { await supabase.auth.signOut(); setLoading(false); return }

      if (linked) {
        setUserType('member')
        setUserData(linked)
      } else {
        setUserType('unknown')
      }
    } catch {
      await supabase.auth.signOut()
    }
    setLoading(false)
  }

  function handleMemberUpdate(updated) {
    setUserData(updated)
  }

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner" />
      </div>
    )
  }

  // ── Not signed in ─────────────────────────────────────────────────────────────
  if (!session) {
    return <Login />
  }

  // ── Admin ─────────────────────────────────────────────────────────────────────
  if (userType === 'admin') {
    return <AdminDashboard adminUser={userData} />
  }

  // ── Member ────────────────────────────────────────────────────────────────────
  if (userType === 'member') {
    // Members must use the app through the Squarespace iframe.
    // If they've somehow reached the Netlify URL directly, redirect them.
    if (!isEmbedded) {
      return (
        <div style={{
          minHeight:      '100vh',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          background:     '#eae6db',
          padding:        '2rem',
          textAlign:      'center',
          fontFamily:     'Georgia, serif',
        }}>
          <div>
            <p style={{ fontSize: '1.1rem', color: '#272626', marginBottom: '1.25rem', lineHeight: 1.7 }}>
              Flock Credit is accessed through the Shepherd's Hut Wines members area.
            </p>
            <a
              href={FLOCK_URL}
              style={{ color: '#272626', fontWeight: 600, fontSize: '0.95rem' }}
            >
              Visit shepherdshutwines.com.au →
            </a>
          </div>
        </div>
      )
    }
    return <MemberDashboard member={userData} onMemberUpdate={handleMemberUpdate} />
  }

  // ── No matching account ───────────────────────────────────────────────────────
  return (
    <div className="app-error-screen">
      <div className="card" style={{ maxWidth: 480, margin: '4rem auto', textAlign: 'center' }}>
        <h2>Account Not Found</h2>
        <p style={{ marginTop: '1rem', color: 'var(--muted)', lineHeight: 1.7 }}>
          The email address <strong>{session.user?.email}</strong> isn't linked to a Flock
          membership yet. If you've just subscribed, please wait a moment and try again.
          Otherwise, contact us at{' '}
          <a href="mailto:info@shepherdshutwines.com.au" style={{ color: 'var(--dark)' }}>
            info@shepherdshutwines.com.au
          </a>
        </p>
        <button
          className="btn btn-secondary"
          style={{ marginTop: '1.5rem' }}
          onClick={() => supabase.auth.signOut()}
        >
          Try a different email
        </button>
      </div>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isJwtError(err) {
  if (!err) return false
  const msg = err.message?.toLowerCase() || ''
  return msg.includes('jwt') || msg.includes('api key')
}
